import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaFire, FaCheckCircle } from "react-icons/fa";
import useDocumentMeta from "../hooks/useDocumentMeta";

export default function Products() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    useDocumentMeta({
        title: t("seo.products.title"),
        description: t("seo.products.description"),
        canonicalPath: "/products",
        robots: "index,follow,max-image-preview:none",
    });

    const products = [
        {
            id: "hardwood-charcoal",
            name: t('products.hardwood.name'),
            description: t('products.hardwood.description'),
            image: "/assets/images/prod10.jpg",
            icon: FaFire,
            specs: [t('products.hardwood.spec1'), t('products.hardwood.spec2'), t('products.hardwood.spec3'), t('products.hardwood.spec4')],
            minOrder: t('products.hardwood.minOrder')
        },
        {
            id: "restaurant-hardwood-charcoal",
            name: t('products.coconut.name'),
            description: t('products.coconut.description'),
            image: "/assets/images/prod9.png",
            icon: FaFire,
            specs: [t('products.coconut.spec1'), t('products.coconut.spec2'), t('products.coconut.spec3'), t('products.coconut.spec4'), t('products.coconut.spec5')],
            minOrder: t('products.coconut.minOrder')
        },
    ];

    return (
        <main className="bg-vsblack text-white min-h-screen pt-20">
            <section className="max-w-7xl mx-auto px-6 py-16">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-5xl md:text-6xl font-bold font-serif mb-4">
                        <span className="text-vsred hero-text-shadow">{t('products.header.title')}</span>
                    </h1>
                    <p className="text-gray-400 text-xl max-w-3xl mx-auto">
                        {t('products.header.description')}
                    </p>
                </motion.div>

                {/* Products Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {products.map((product, index) => (
                        <motion.div
                            key={product.id}
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            whileHover={{ y: -5, scale: 1.02 }}
                            className="group cursor-pointer card-dark overflow-hidden rounded-xl shadow-dark transition-all"
                            onClick={() => navigate(`/products/${product.id}`)}
                        >
                            {/* Image */}
                            <div className="relative h-[600px] overflow-hidden">
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-vsblackLight via-transparent to-transparent group-hover:opacity-70 transition-opacity" />

                                {/* Icon badge */}
                                <div className="absolute top-4 right-4 w-14 h-14 bg-gradient-to-br from-vsred to-vsred-dark rounded-xl flex items-center justify-center shadow-red-glow">
                                    <product.icon className="text-2xl text-white" />
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 flex flex-col h-full">
                                <h2 className="text-2xl font-bold mb-3 font-serif text-vsred hero-text-shadow group-hover:text-white transition-colors">
                                    {product.name}
                                </h2>
                                <p className="text-gray-400 mb-6 leading-relaxed group-hover:text-gray-200 transition-colors">
                                    {product.description}
                                </p>

                                {/* Specs */}
                                <div className="space-y-2 mb-6 flex-1">
                                    {product.specs.map((spec, i) => (
                                        <div key={i} className="flex items-start gap-2 text-sm text-gray-300 group-hover:text-gray-200 transition-colors">
                                            <FaCheckCircle className="text-vsred mt-0.5 flex-shrink-0" />
                                            <span>{spec}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                                    <div className="text-sm">
                                        <span className="text-gray-500">{t('products.minOrder')}</span>
                                        <span className="text-vsred-light font-semibold ml-2">{product.minOrder}</span>
                                    </div>
                                    <span className="text-sm text-vsred-light font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                                        {t('products.learnMore')} →
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* CTA Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="mt-20 text-center card-dark p-12"
                >
                    <h3 className="text-3xl font-bold font-serif mb-4 text-white">
                        {t('products.cta.title')}
                    </h3>
                    <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
                        {t('products.cta.description')}
                    </p>
                    <a
                        href="#quote"
                        className="btn-primary inline-block text-lg"
                        onClick={(e) => {
                            e.preventDefault();
                            window.location.href = '/#quote';
                        }}
                    >
                        {t('products.cta.button')}
                    </a>
                </motion.div>
            </section>
        </main>
    );
}
